// Record Shares admin page
// View and manage all manual shares across the system
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TrashIcon } from '@heroicons/react/20/solid'
import { apiRequest } from '../../lib/api'
import { useTranslation } from '../../hooks/useTranslation'
import { useAuth } from '../../hooks/useAuth'
import { useAssignableUsers } from '../../hooks/useUsers'
import { Avatar } from '../../components/ui/Avatar'

interface RecordShare {
  id: string
  entity: string
  entityId: string
  sharedWithUserId: string
  sharedByUserId: string
  accessLevel: 'read' | 'read_write'
  reason?: string
  expiresAt?: string
  createdAt: string
  sharedWithUser: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
  sharedByUser?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
}

interface SharesResponse {
  data: RecordShare[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const ENTITIES = ['leads', 'accounts', 'contacts', 'opportunities', 'events', 'notes'] as const

export function RecordSharesPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [entityFilter, setEntityFilter] = useState<string>('')
  const [userFilter, setUserFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const limit = 20

  const { data: usersData } = useAssignableUsers()
  const users = usersData?.data || []

  // Build query params
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', String(limit))
  if (entityFilter) params.set('entity', entityFilter)
  if (userFilter) params.set('userId', userFilter)

  const { data: sharesData, isLoading } = useQuery({
    queryKey: ['record-shares', 'all', entityFilter, userFilter, page],
    queryFn: () => apiRequest<SharesResponse>(`/record-shares?${params.toString()}`),
  })

  const deleteShare = useMutation({
    mutationFn: (shareId: string) =>
      apiRequest(`/record-shares/${shareId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['record-shares'] })
    },
  })

  const shares = sharesData?.data || []
  const total = sharesData?.total || 0
  const totalPages = sharesData?.totalPages || 1

  const canManage = user?.role === 'admin'

  const getEntityLink = (entity: string, entityId: string) => {
    return `/${entity}/${entityId}`
  }

  const formatUserName = (user?: { firstName?: string | null; lastName?: string | null; email: string } | null) => {
    if (!user) return '-'
    return user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
  }

  // Count shares by entity
  const entityCounts = shares.reduce((acc, share) => {
    acc[share.entity] = (acc[share.entity] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  if (!canManage) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('nav.recordShares')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('pages.recordShares.subtitle')}</p>
            </div>
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6">
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-gray-900">{t('common.accessDenied')}</h2>
              <p className="mt-2 text-sm text-gray-500">{t('users.adminOnly')}</p>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
        </aside>
        <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
          <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] flex items-center justify-center">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Secondary column - Filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('nav.recordShares')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('pages.recordShares.subtitle')}</p>
          </div>

          {/* Entity Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('sharingRules.filterByEntity')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setEntityFilter(''); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  entityFilter === ''
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')} ({total})
              </button>
              {ENTITIES.map(entity => (
                <button
                  key={entity}
                  onClick={() => { setEntityFilter(entity); setPage(1) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    entityFilter === entity
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`nav.${entity}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* User Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')} {t('fields.user')}</h3>
            <select
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
            >
              <option value="">{t('common.all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {formatUserName(user)}
                </option>
              ))}
            </select>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('pages.leads.stats.title')}</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pages.leads.stats.total')}</dt>
                <dd className="font-medium text-gray-900">{total}</dd>
              </div>
              {entityFilter && (
                <div className="flex justify-between text-sm">
                  <dt className="text-gray-500">{t('common.filtered')}</dt>
                  <dd className="font-medium text-gray-900">{shares.length}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('nav.recordShares')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('pages.recordShares.subtitle')}</p>

            {/* Mobile filters */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => { setEntityFilter(''); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  entityFilter === ''
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              {ENTITIES.map(entity => (
                <button
                  key={entity}
                  onClick={() => { setEntityFilter(entity); setPage(1) }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    entityFilter === entity
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`nav.${entity}` as any)}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t('fields.entity')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharing.sharedWith')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharing.accessLevel')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('fields.sharedBy')}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t('sharing.expiresAt')}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {shares.map((share) => (
                        <tr key={share.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <Link
                              to={getEntityLink(share.entity, share.entityId)}
                              className="text-brand-600 hover:text-brand-900"
                            >
                              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                                {t(`nav.${share.entity}` as any)}
                              </span>
                              <span className="text-gray-400 text-xs ml-2">#{share.entityId.slice(0, 8)}</span>
                            </Link>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="flex items-center gap-2">
                              <Avatar
                                firstName={share.sharedWithUser.firstName || '?'}
                                lastName={share.sharedWithUser.lastName}
                                size="sm"
                              />
                              <span className="text-gray-900">
                                {formatUserName(share.sharedWithUser)}
                              </span>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                              share.accessLevel === 'read_write'
                                ? 'bg-green-50 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {share.accessLevel === 'read_write' ? t('sharing.readWrite') : t('sharing.readOnly')}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {share.sharedByUser ? (
                              <div className="flex items-center gap-2">
                                <Avatar
                                  firstName={share.sharedByUser.firstName || '?'}
                                  lastName={share.sharedByUser.lastName}
                                  size="xs"
                                />
                                <span>{formatUserName(share.sharedByUser)}</span>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {share.expiresAt ? (
                              <span className={new Date(share.expiresAt) < new Date() ? 'text-red-600' : 'text-orange-600'}>
                                {new Date(share.expiresAt).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button
                              onClick={() => {
                                if (confirm(t('sharing.confirmRemove'))) {
                                  deleteShare.mutate(share.id)
                                }
                              }}
                              disabled={deleteShare.isPending}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {shares.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">{t('pages.recordShares.noShares')}</p>
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <p className="text-sm text-gray-700">
                        {t('common.showingOf', { count: shares.length, total })}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                        >
                          {t('common.previous')}
                        </button>
                        <span className="px-3 py-1 text-sm">
                          {page} / {totalPages}
                        </span>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 hover:bg-gray-50"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
